import React, { useEffect, useRef, useState } from "react";
import { Drawer } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Employee } from "../../../types/salon";
import { CommissionRecord } from "../../../types/commission";
import UnsavedChangesDialog from "../../common/UnsavedChangesDialog/UnsavedChangesDialog";
import { CommissionFormController } from "../NewCommissionForm";
import CommissionFormView from "./CommissionFormView";
import RateDetailsView from "./RateDetailsView";
import { staffName } from "./commissionDisplay";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";

/** Matches ResponsiveContext's mobile breakpoint. */
export const MOBILE_QUERY = "(max-width: 768px)";

/** Drawer width on desktop (design handoff); full screen on phones. */
export const DRAWER_WIDTH = 720;

/** What to open: a staff member's rates, or the add form (with a team-member
 * picker when `staffId` is null). */
export interface CommissionDrawerRequest {
  staffId: string | null;
  view: "details" | "add";
}

type DrawerScreen =
  | { kind: "details"; staffId: string }
  | {
      kind: "form";
      staffId: string | null;
      editing: CommissionRecord | null;
      /** Opened from the rate details (back returns there). */
      fromDetails: boolean;
    };

const screenFor = (request: CommissionDrawerRequest): DrawerScreen =>
  request.view === "details" && request.staffId
    ? { kind: "details", staffId: request.staffId }
    : {
        kind: "form",
        staffId: request.staffId,
        editing: null,
        fromDetails: false,
      };

/** "Ana Lee's commission rate" — what the unsaved-changes dialog names. */
const formName = (
  screen: DrawerScreen | null,
  employees: Employee[],
): string => {
  const employee =
    screen?.staffId && employees.find((e) => e.id === screen.staffId);
  return employee
    ? `${staffName(employee)}’s commission rate`
    : "this commission rate";
};

export interface CommissionRateDrawerProps {
  /** null closes the drawer. */
  request: CommissionDrawerRequest | null;
  onClose: () => void;
  employees: Employee[];
  commissionsByStaff: Record<string, CommissionRecord[]>;
  canEdit: boolean;
  onDelete: (record: CommissionRecord) => void;
  /** A rate was saved: refresh the list. */
  onSaved: () => void;
}

/**
 * Settings › Commissions drawer. Shows one staff member's rates (the rate in
 * force and past rates) and hosts adding or editing a rate — including the
 * "Rates by service" screen — without leaving the drawer. 720px wide on
 * desktop, full screen on phones. Leaving a changed add/edit form (Cancel,
 * back, ×, Esc or a click outside) asks to save or discard first.
 */
const CommissionRateDrawer: React.FC<CommissionRateDrawerProps> = ({
  request,
  onClose,
  employees,
  commissionsByStaff,
  canEdit,
  onDelete,
  onSaved,
}) => {
  const isMobile = useMediaQuery(MOBILE_QUERY) ?? false;
  const [screen, setScreen] = useState<DrawerScreen | null>(null);
  const form = useRef<CommissionFormController>(null);
  const guard = useUnsavedChangesGuard(
    () => form.current?.submit() ?? Promise.resolve(false),
  );
  const { setDirty } = guard;
  useEffect(() => {
    if (request) setScreen(screenFor(request));
    setDirty(false);
  }, [request, setDirty]);

  const recordsFor = (staffId: string | null): CommissionRecord[] =>
    (staffId && commissionsByStaff[staffId]) || [];
  // A new screen starts clean: a form reports its own changes once mounted.
  const showDetails = (staffId: string) => {
    setDirty(false);
    setScreen({ kind: "details", staffId });
  };
  const openForm = (staffId: string, editing: CommissionRecord | null) => {
    setDirty(false);
    setScreen({ kind: "form", staffId, editing, fromDetails: true });
  };

  const renderScreen = (): React.ReactNode => {
    if (!screen) return null;
    if (screen.kind === "form") {
      const { staffId, fromDetails } = screen;
      const back =
        fromDetails && staffId ? () => showDetails(staffId) : undefined;
      return (
        <CommissionFormView
          employees={employees}
          commissionsByStaff={commissionsByStaff}
          editing={screen.editing}
          staffId={staffId}
          onBack={back && (() => guard.attempt(back))}
          onCancel={() => guard.attempt(back ?? onClose)}
          onSaved={(savedStaffId) => {
            onSaved();
            showDetails(savedStaffId);
          }}
          onDirtyChange={setDirty}
          controllerRef={form}
        />
      );
    }
    const employee = employees.find((e) => e.id === screen.staffId);
    if (!employee) return null;
    return (
      <RateDetailsView
        key={employee.id}
        employee={employee}
        records={recordsFor(employee.id)}
        canEdit={canEdit}
        isMobile={isMobile}
        onAddNew={() => openForm(employee.id, null)}
        onEdit={(record) => openForm(employee.id, record)}
        onDelete={onDelete}
      />
    );
  };

  return (
    <>
      <Drawer.Root
        opened={request !== null}
        onClose={() => guard.attempt(onClose)}
        position="right"
        size={isMobile ? "100%" : DRAWER_WIDTH}
      >
        <Drawer.Overlay />
        <Drawer.Content>
          <Drawer.Body p={0} h="100%">
            {renderScreen()}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
      <UnsavedChangesDialog
        opened={guard.confirming}
        name={formName(screen, employees)}
        description="You changed this commission rate. Save it, or discard your changes."
        saveLabel={
          screen?.kind === "form" && screen.editing
            ? "Save Changes"
            : "Save Rate"
        }
        saving={guard.saving}
        onSave={guard.save}
        onDiscard={guard.discard}
        onKeepEditing={guard.keepEditing}
      />
    </>
  );
};

export default CommissionRateDrawer;
